/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { Dispatch, SetStateAction, useEffect, useRef } from "react";

import styled from "styled-components";

import { SlashMenuOption } from "./slashMenuOptions";

interface Props {
  option: SlashMenuOption;
  selected: boolean;
  index: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  inputType: "mouse" | "keyboard";
}

export const SlashOption = ({ option, selected, index, setSelectedIndex, inputType }: Props) => {
  // option ref
  const ref = useRef<HTMLDivElement>(null);

  // when selected scroll option into view
  useEffect(() => {
    if (selected && inputType === "keyboard") {
      const optionElement = ref.current;
      if (optionElement) {
        optionElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [selected]);

  const handleMouseEnter = () => {
    // If input type is mouse, set selected index. If keyboard, do nothing. this prevents the option from being "selected" by
    // mouse when the mouse is hovering over the menu as you try to navigate it with the keyboard
    if (inputType === "mouse") {
      setSelectedIndex(index);
    }
  };

  return (
    <SlashMenuOptionWrapper $selected={selected} ref={ref} onMouseEnter={handleMouseEnter}>
      <IconWrapper>{option.icon}</IconWrapper>
      <OptionInfo>
        <OptionName>{option.name}</OptionName>
        <OptionDescription>{option.description}</OptionDescription>
      </OptionInfo>
    </SlashMenuOptionWrapper>
  );
};

const SlashMenuOptionWrapper = styled.div<{ $selected: boolean }>`
  align-items: center;
  display: flex;
  white-space: nowrap;
  padding: 14px;
  background-color: ${({ $selected, theme }) => $selected && theme.subtleContrast};
  cursor: pointer;
  scroll-margin: 60px;
`;

const IconWrapper = styled.div`
  height: 20px;
  width: 20px;
  margin-right: 17px;
  color: ${({ theme }) => theme.primary};
`;

const OptionInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionName = styled.div`
  color: ${({ theme }) => theme.text.default};
  font-size: 14px;
`;

const OptionDescription = styled.div`
  color: ${({ theme }) => theme.text.subtle};
  font-size: 10px;
`;
